import {
  MenuList,
  MenuItem,
  MenuDivider,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DeleteFilled, CopyRegular, ColorFilled } from '@fluentui/react-icons';
import { useEffect, useRef } from 'react';
import { NOTE_COLOR_KEYS, getNoteColors } from '@/types/note';
import { ottoTokens } from '@/theme/ottoTokens';
import { useAppStore } from '@/stores/appStore';

const useStyles = makeStyles({
  menuContainer: {
    position: 'fixed',
    zIndex: 1000,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    minWidth: '180px',
  },
  colorLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '5px 12px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  colorSubmenu: {
    display: 'flex',
    flexDirection: 'row',
    gap: '6px',
    padding: '8px 12px',
  },
  colorSwatch: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    cursor: 'pointer',
    transitionProperty: 'transform',
    transitionDuration: '0.1s',
    ':hover': {
      transform: 'scale(1.15)',
    },
  },
  colorSwatchSelected: {
    boxShadow: ottoTokens.noteBorderSelected,
  },
});

interface NoteContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number } | null;
  noteId: string | null;
  currentColor: string | null;
  onClose: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onChangeColor: (color: string) => void;
}

export const NoteContextMenu = ({
  isOpen,
  position,
  noteId,
  currentColor,
  onClose,
  onDelete,
  onDuplicate,
  onChangeColor,
}: NoteContextMenuProps) => {
  const styles = useStyles();
  const menuRef = useRef<HTMLDivElement>(null);
  const theme = useAppStore((s) => s.theme);
  const isDarkMode = theme === 'dark';

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen || !position || !noteId) return null;

  return (
    <div
      ref={menuRef}
      className={styles.menuContainer}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <MenuList>
        <MenuItem
          icon={<CopyRegular />}
          onClick={() => {
            onDuplicate();
            onClose();
          }}
        >
          Duplicate Note
        </MenuItem>
        <MenuDivider />
        <div className={styles.colorLabel}>
          <ColorFilled />
          <span>Color</span>
        </div>
        <div className={styles.colorSubmenu}>
          {NOTE_COLOR_KEYS.map((colorKey) => {
            const colors = getNoteColors(colorKey, isDarkMode);
            return (
              <div
                key={colorKey}
                className={`${styles.colorSwatch} ${currentColor === colorKey ? styles.colorSwatchSelected : ''}`}
                style={{
                  backgroundColor: colors.background,
                  border: `1px solid ${colors.border}`,
                }}
                onClick={() => {
                  onChangeColor(colorKey);
                  onClose();
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onChangeColor(colorKey);
                    onClose();
                  }
                }}
                aria-label={`Color ${colorKey}`}
              />
            );
          })}
        </div>
        <MenuDivider />
        <MenuItem
          icon={<DeleteFilled />}
          onClick={() => {
            onDelete();
            onClose();
          }}
        >
          Delete Note
        </MenuItem>
      </MenuList>
    </div>
  );
};
